import { QuantityChangedFunction } from 'components/Cart/CartItems';
import Checkout from 'components/Cart/Checkout';
import Confirm from 'components/Cart/Confirm';
import Empty from 'components/Cart/Empty';
import PaymentStep from 'components/Cart/Payment/PaymentStep';
import Flex from 'components/ui/Flex';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { debounce } from 'lodash';
import { CartInput, CartItemSkuAddOn } from 'microshop-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router-dom';
import {
    Valid,
    addChangeComment,
    addChangeSku,
    addressValidated,
    deliverySet,
    loadCart,
    removeFromCart,
} from 'store/reducers/cartSlice';

const Cart = () => {
    const { loading, cart } = useAppSelector(({ cart }) => cart);
    const dispatch = useAppDispatch();
    const { options } = useAppSelector(({ user }) => ({
        options: user.userIdentity?.orderOptions,
    }));

    const CartView = { Cart: 0, Confirm: 1, Payment: 2 };
    const location = useLocation();
    const initialView = location.state?.view === 'confirm' ? CartView.Confirm : CartView.Cart;
    const [cartPageState, setCartPageState] = useState({ view: initialView, confirm: null });

    useEffect(() => {
        if (options?.deliveryAddresses && options?.deliveryAddresses?.length > 0) {
            dispatch(
                deliverySet({
                    name: 'addressId',
                    value: options.deliveryAddresses?.[0].id ?? '',
                }),
            );
            dispatch(addressValidated({ valid: Valid.Valid }));
        }
    }, [options, dispatch]);

    const confirmed = () => {
        setCartPageState({ ...cartPageState, view: CartView.Confirm });
    };

    const proceedToPayment = () => {
        setCartPageState({ ...cartPageState, view: CartView.Payment });
    };

    const backToCart = async () => {
        await dispatch(loadCart());
        setCartPageState((state) => ({ ...state, view: CartView.Cart }));
    };

    const onChangeQuantity: QuantityChangedFunction = ({
        sku,
        qty,
        split,
        collectionId,
        addons,
        inputs,
        splitId,
        immediate,
    }) => {
        if (Number.isNaN(qty) || typeof sku !== 'string') return;

        if (immediate) {
            dispatchToServer(sku, qty, addons, collectionId, split, splitId, inputs);
        } else {
            debouncedHandleQtyChanged(sku, qty, addons, collectionId, split, splitId, inputs);
        }
    };

    const dispatchToServer = useCallback(
        (
            sku: string,
            quantity: number,
            addOns?: CartItemSkuAddOn[],
            collectionId?: number | null,
            split?: string | null,
            splitId?: number | undefined,
            inputs?: CartInput[],
        ) => dispatch(addChangeSku({ sku, collectionId, split, splitId, quantity, addOns, inputs })),
        [dispatch],
    );

    const debouncedHandleQtyChanged = useMemo(() => debounce(dispatchToServer, 600), [dispatchToServer]);

    const onSetComment = (skuPattern: string, comment: string) => {
        dispatch(addChangeComment({ skuPattern, comment }));
    };

    const onRemove = (sku: string) => {
        dispatch(removeFromCart({ sku }));
    };

    const isLoaded = !loading;
    const isEmpty = (cart?.quantity ?? 0) === 0;

    if (loading && isEmpty) {
        return <Skeleton height={420} className="p-3" />;
    }

    return (
        <Flex column className="pt-4 pb-2 px-3">
            {cartPageState.view === CartView.Cart && !isEmpty && (
                <Checkout
                    cart={cart}
                    stuck={true}
                    onChangeQuantity={onChangeQuantity}
                    onRemove={onRemove}
                    onSetComment={onSetComment}
                    onConfirm={confirmed}
                    onProceedToPayment={proceedToPayment}
                />
            )}
            {cartPageState.view === CartView.Payment && <PaymentStep onBack={backToCart} onPaid={confirmed} />}
            {cartPageState.view === CartView.Confirm && <Confirm onBack={backToCart}/>}
            {isEmpty && isLoaded && cartPageState.view === CartView.Cart && <Empty />}
        </Flex>
    );
};

export default Cart;
